import { useCallback, useEffect, useRef } from 'react';
import { getMapContainerWidth, zoomAndPan } from '../utils';
import { Selection } from 'd3';
import * as d3 from 'd3';
import { debounce } from 'lodash';
import { useAppDispatch } from '../../../../redux/hooks';
import { setChartSettingGeoMapPositionAction } from '../../../../redux/slices/analyses';
import GeoMapRightArrowIcon from '../../../../assets/icons/geo-map-arrow.svg';
import GeoHomeIcon from '../../../../assets/icons/geo-home.svg';
import GeoZoomInIcon from '../../../../assets/icons/geo-zoom-in.svg';
import GeoZoomOutIcon from '../../../../assets/icons/geo-zoom-out.svg';
import { useUserSettingsSelector } from '../../../../redux/selectorHooks';
import { IGeoMapTransform } from '../../interfaces';
import { ControlPanelDirection } from '../types';
import { theme } from '../../../../styles/theme';
import { defaultGeoMapTransform } from '../../../../redux/utils/chartSettings';
import { useChartContext } from '../../ChartContext';

interface IProps {
  svgMapRef: React.MutableRefObject<
    Selection<SVGSVGElement, unknown, Element, unknown> | undefined
  >;
  analysisId: string;
  containerId: string;
  isGeoMapReady: boolean;
}

const ControlPanel = ({
  analysisId,
  svgMapRef,
  containerId,
  isGeoMapReady,
}: IProps) => {
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();
  const windowSize = useUserSettingsSelector('windowSize');
  const {
    settings: { chartSettings },
  } = useChartContext();
  const { geoMapConfig } = chartSettings;
  // read once, not need to update this component when geoMapConfig changes
  const geoState = useRef({
    x: geoMapConfig.x,
    y: geoMapConfig.y,
    k: geoMapConfig.k,
  });
  const dispatch = useAppDispatch();

  const storeViewState = useCallback(
    (transform: IGeoMapTransform) => {
      const debouncedDispatch = debounce((t: IGeoMapTransform) => {
        dispatch(
          setChartSettingGeoMapPositionAction({
            analysisId,
            geoMapConfig: {
              x: t.x,
              y: t.y,
              k: t.k,
            },
          }),
        );
      }, 2000);

      debouncedDispatch(transform);
    },
    [analysisId, dispatch],
  );

  const mapMoveHandler = useCallback(
    (direction: ControlPanelDirection) => {
      if (zoomRef.current && svgMapRef.current) {
        const svgRefNode = svgMapRef?.current?.node();
        if (!svgRefNode) {
          return;
        }
        const currentTransform = d3.zoomTransform(svgRefNode);
        const scale = currentTransform.k;
        const translateX = currentTransform.x;
        const translateY = currentTransform.y;
        const panAmount = 100;

        let newX = translateX;
        let newY = translateY;

        switch (direction) {
          case 'up':
            newY += panAmount;
            break;
          case 'down':
            newY -= panAmount;
            break;
          case 'left':
            newX += panAmount;
            break;
          case 'right':
            newX -= panAmount;
            break;
          default:
            break;
        }
        svgMapRef.current
          .transition()
          .duration(200)
          .call(
            zoomRef.current.transform,
            d3.zoomIdentity.translate(newX, newY).scale(scale),
          );
      }
    },
    [svgMapRef, zoomRef],
  );

  const handleReset = useCallback(() => {
    if (svgMapRef.current && zoomRef.current) {
      svgMapRef.current
        .transition()
        .duration(250)
        .call(zoomRef.current.transform, d3.zoomIdentity);
      storeViewState(defaultGeoMapTransform);
    }
  }, [svgMapRef, zoomRef, storeViewState]);

  const handleZoom = useCallback(
    (zoomType: 'in' | 'out') => {
      if (svgMapRef.current && zoomRef.current) {
        const svgRefNode = svgMapRef.current.node();
        if (!svgRefNode) {
          return;
        }
        const currentTransform = d3.zoomTransform(svgRefNode);
        const newScale =
          zoomType === 'in'
            ? currentTransform.k * 1.6
            : currentTransform.k / 1.6;

        svgMapRef.current
          .transition()
          .duration(200)
          .call(zoomRef.current.scaleTo as () => void, newScale);

        const newTransform = d3.zoomTransform(svgRefNode);
        storeViewState(newTransform);
      }
    },
    [svgMapRef, zoomRef, storeViewState],
  );

  const addDirectionControl = useCallback(
    (
      x: number,
      y: number,
      direction: ControlPanelDirection,
      controls: Selection<SVGGElement, unknown, HTMLElement, unknown>,
    ) => {
      const arrow = controls
        .append('g')
        .attr('transform', `translate(${x}, ${y})`)
        .style('cursor', 'pointer')
        .on('click', () => mapMoveHandler(direction))
        .on('dblclick', (e) => e.stopPropagation());

      const rotationAngle = {
        up: -90,
        left: 180,
        right: 0,
        down: 90,
      }[direction];

      arrow
        .append('image')
        .attr('xlink:href', GeoMapRightArrowIcon)
        .attr('width', 14)
        .attr('height', 14)
        .attr('transform', `rotate(${rotationAngle}, 7, 7)`);
    },
    [mapMoveHandler],
  );

  const addZoomButton = useCallback(
    (
      y: number,
      zoomType: 'in' | 'out',
      zoomButtons: Selection<SVGGElement, unknown, HTMLElement, unknown>,
    ) => {
      const button = zoomButtons
        .append('g')
        .attr('transform', `translate(0, ${y})`)
        .style('cursor', 'pointer')
        .on('click', () => handleZoom(zoomType))
        .on('dblclick', (e) => e.stopPropagation());

      button
        .append('image')
        .attr('xlink:href', zoomType === 'in' ? GeoZoomInIcon : GeoZoomOutIcon)
        .attr('width', 24)
        .attr('height', 24);
    },
    [handleZoom],
  );

  // update the svg width when window size changes
  // reset the view to default when window size changes
  useEffect(() => {
    const width = getMapContainerWidth(containerId);
    const rootSvg = svgMapRef.current;
    if (rootSvg) {
      rootSvg.attr('width', width);
    }
  }, [windowSize?.width, containerId, svgMapRef]);

  // add the control panel when the geo map is ready,
  // this useEffect will only run once when the component is mounted and isGeoMapReady is true
  useEffect(() => {
    if (svgMapRef.current && isGeoMapReady) {
      const container = d3
        .select(`#${containerId}`)
        .select<SVGSVGElement>('svg');
      zoomRef.current = zoomAndPan({
        svg: container,
        transform: geoState.current,
        storeViewState,
      });

      // add container for the control panel
      container
        .append('rect')
        .attr('x', 5)
        .attr('y', 5)
        .attr('width', 84)
        .attr('height', 160)
        .attr('fill', theme.colors.level0)
        .attr('stroke', theme.colors.border)
        .attr('stroke-width', 1)
        .attr('opacity', 0.7)
        .attr('rx', 5)
        .attr('ry', 5)
        .style('pointer-events', 'none');

      // Add arrow controls
      const controlSize = 30;
      const controlPadding = 10;
      const controls = container
        .append('g')
        .attr('transform', `translate(${controlPadding}, 10)`);
      addDirectionControl(controlSize, 0, 'up', controls);
      addDirectionControl(0, controlSize, 'left', controls);
      addDirectionControl(2 * controlSize, controlSize, 'right', controls);
      addDirectionControl(controlSize, 2 * controlSize, 'down', controls);

      // Add reset button in the middle of 4 pan buttons
      const resetButton = controls
        .append('g')
        .attr('transform', `translate(${controlSize}, ${controlSize})`)
        .style('cursor', 'pointer')
        .on('click', handleReset)
        .on('dblclick', (e) => e.stopPropagation());

      resetButton
        .append('image')
        .attr('xlink:href', GeoHomeIcon)
        .attr('width', 20)
        .attr('height', 20)
        .attr('transform', `translate(-3, -3)`);

      // Add zoom in and zoom out buttons
      const zoomButtonSize = 30;
      const zoomButtons = container
        .append('g')
        // below the move panel
        .attr('transform', `translate(38, 100)`);

      addZoomButton(0, 'in', zoomButtons);
      addZoomButton(zoomButtonSize + 5, 'out', zoomButtons);
    }
  }, [
    geoState,
    addDirectionControl,
    storeViewState,
    svgMapRef,
    addZoomButton,
    containerId,
    isGeoMapReady,
    handleReset,
  ]);

  return <></>;
};

export default ControlPanel;
